import { useCallback, useMemo } from 'react';
import { computeMessageBytes } from './useMessageLength';
import * as Yup from 'yup';
import { KakaoButton } from '../Components/Common/ButtonsEditor';

export type ChannelType = 'AT' | 'FT' | 'SM' | 'LM';

interface ValidationInput {
  channel: ChannelType;
  site_id?: number;
  template_id?: number; // required for AT
  message: string;
  buttons: KakaoButton[];
  image?: { url: string; is_wide?: boolean } | null;
  recipientsCount: number;
  maxRecipients: number;
  fall_back_message?: string;
}

export interface ValidationResult {
  valid: boolean;
  errors: Record<string, string>;
}

const urlRegex = /^(https?:\/\/)[\w.-]+(\.[\w\.-]+)+(\/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?$/i;

export function useMessageValidation() {
  const baseSchema = useMemo(() => Yup.object({
    channel: Yup.mixed<ChannelType>().oneOf(['AT','FT','SM','LM']).required('채널 필요'),
    site_id: Yup.number().optional(),
    template_id: Yup.number().when('channel', {
      is: 'AT',
      then: schema => schema.required('템플릿 필수 (알림톡)'),
      otherwise: schema => schema.optional()
    }),
    message: Yup.string().trim().required('메시지 내용 필요').test('byte-limits','메시지 길이 제한 초과', function(val){
      if (!val) return false;
      const bytes = computeMessageBytes(val);
      const channel: ChannelType = (this.parent as any).channel;
      if (channel === 'SM' && bytes > 90) return false; // should become LMS automatically in UI logic
      if ((channel === 'LM' || channel === 'SM') && bytes > 2000) return false;
      if ((channel === 'AT' || channel === 'FT') && val.length > 1000) return false;
      return true;
    }),
    recipientsCount: Yup.number().min(1, '수신자 선택 필요'),
    maxRecipients: Yup.number().min(1).required(),
    fall_back_message: Yup.string().when(['channel','message'], {
      is: (channel: ChannelType) => channel === 'AT',
      then: s => s.max(90, '대체문구 90자 이내'),
      otherwise: s => s.strip()
    })
  }), []);

  const buttonSchema = useMemo(() => Yup.object({
    name: Yup.string().trim().required('버튼명 필요').max(14, '14자 이내'),
    type: Yup.mixed().oneOf(['WL','AL','DS','BK','MD','AC']).required(),
    url_mobile: Yup.string().when('type', {
      is: 'WL',
      then: s => s.required('모바일 URL 필요').matches(urlRegex, 'URL 형식 오류'),
      otherwise: s => s.strip()
    }),
    url_pc: Yup.string().when('type', {
      is: 'WL',
      then: s => s.matches(urlRegex, 'URL 형식 오류').nullable().optional(),
      otherwise: s => s.strip()
    }),
    scheme_android: Yup.string().when('type', {
      is: 'AL',
      then: s => s.required('Android 스킴 필요'),
      otherwise: s => s.strip()
    }),
    scheme_ios: Yup.string().when('type', {
      is: 'AL',
      then: s => s.required('iOS 스킴 필요'),
      otherwise: s => s.strip()
    })
  }), []);

  const fullSchema = useMemo(() => baseSchema.concat(Yup.object({
    buttons: Yup.array().of(buttonSchema).max(5, '버튼은 최대 5개').test('buttons-channel-rules', '채널 버튼 규칙 위반', function(btns) {
      const channel: ChannelType = this.parent.channel;
      if (!btns || btns.length === 0) return true; // buttons optional overall
      if (channel === 'SM' || channel === 'LM') return false; // no buttons allowed
      return true;
    }),
    image: Yup.mixed().test('image-ft-only', '친구톡만 이미지 사용', function(val) {
      const channel: ChannelType = this.parent.channel;
      if (val && channel !== 'FT') return false;
      return true;
    })
  })), [baseSchema, buttonSchema]);

  const validate = useCallback(async (input: ValidationInput): Promise<ValidationResult> => {
    try {
      await fullSchema.validate({ ...input, recipientsCount: input.recipientsCount, maxRecipients: input.maxRecipients }, { abortEarly: false });
      return { valid: true, errors: {} };
    } catch (e: any) {
      if (e.inner) {
        const errs: Record<string, string> = {};
        e.inner.forEach((err: any) => {
          if (err.path && !errs[err.path]) errs[err.path] = err.message;
        });
        return { valid: false, errors: errs };
      }
      return { valid: false, errors: { _form: e.message } };
    }
  }, [fullSchema]);

  return { validate };
}

export default useMessageValidation;
